import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stack, Typography } from '@mui/material';
import { getGeneralProfileResults } from 'API/base-profile';
import {
  analyzeLinkedinContacts,
  createLinkedinProject,
  getAnalyzedLinkedinContacts,
  getGroupProfile,
  getLinkedinSearchResults,
} from 'API/linkedinproject';
import { createOccupation } from 'API/occupation';
import { addProfile } from 'API/profile';
import { createProjectWrapper } from 'API/project-wrapper';
import { isSameDay, parseISO } from 'date-fns';
import { CookieErrors, SortDirection } from 'enums';
import LinkedinCrawlerOverview from 'features/linkedin-crawler/linkedin-crawler-overview/linkedin-crawler-overview';
import { LinkedinProject, personas } from 'features/linkedin-crawler/profile-generator/profile-generator.types';
import SearchDurationInfoDialog from 'features/linkedin-crawler/profile-generator/search-duration-info-dialog';
import PlaceholderElement from 'features/text-generation/placeholder-element';
import PaiActionText from 'features/ui/base-components/pai-action-text';
import PaiLink from 'features/ui/base-components/pai-link';
import saveAs from 'file-saver';
import useLinkedinCookies from 'hooks/use-linkedin-cookies';
import i18n from 'i18n';
import JSZip from 'jszip';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import useLimitReachedStore from 'stores/limit-reached-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import { Contact } from 'types/contact.types';
import { CookieError } from 'types/cookies.types';
import { ProfileValues } from 'types/profile.types';
import CookieDialog from '../features/linkedin-crawler/profile-generator/cookie-error-dialog';
import StepTwoUrlFinderProjects from '../features/linkedin-crawler/profile-generator/create-classification-project-from-search-projects';
import CreateGlobalProfileDialog from '../features/linkedin-crawler/profile-generator/create-global-profile-dialog';
import CreateManualProfile from '../features/linkedin-crawler/profile-generator/create-manual-profile';
import CreateManualProfileSuccess from '../features/linkedin-crawler/profile-generator/create-manual-profile-success';
import CreateProfile from '../features/linkedin-crawler/profile-generator/create-profile';
import DefineLinkedinProfiles from '../features/linkedin-crawler/profile-generator/define-linkedin-profiles';
import EnterProfileNameDialog from '../features/linkedin-crawler/profile-generator/enter-profile-name-dialog';
import EvaluateClassificationProjects from '../features/linkedin-crawler/profile-generator/evaluate-classification-projects';
import MaxContactsDialog from '../features/linkedin-crawler/profile-generator/max-contacts-dialog';
import ProjectResultPreview from '../features/linkedin-crawler/profile-generator/project-result-preview';
import { useProjectWrappers } from '../features/linkedin-crawler/profile-generator/use-project-wrappers';
import { useQueryProjectWrapper } from '../features/linkedin-crawler/profile-generator/use-query-project-wrapper';
import EnterProjectNameDialog from '../features/text-generation/enter-project-name-dialog';
import { OptionType } from '../features/ui/base-components/pai-auto-complete';
import PaiSuccessDialog from '../features/ui/base-dialogs/pai-succes-dialog';

enum StepStatuses {
  INITIAL = 'initial',
  ACTIVE = 'active',
  DONE = 'done',
}

enum Attributes {
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  COMPANY = 'companyName',
  TYPE = 'type',
}

const ProfileGenerator: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleOpenLimitReachedDialog = useLimitReachedStore((state) => state.handleOpenLimitReachedDialog);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const appBarNavigation = useAppBarNavigationStore((state) => state.activeTab);

  const defaultProfile: ProfileValues = {
    jointPosPower: 2,
    jointPosAchieve: 2,
    jointPosAffil: 2,
    authenticity: 2,
    regFocus: 2,
  };

  const {
    urlFinderProjects,
    setUrlFinderWrapperQuery,
    classificationProjects,
    classificationProjectName,
    setClassificationWrapperQuery,
    fetchClassificationProjectsFromWrapper,
    resetQuery,
  } = useQueryProjectWrapper();

  const [manualCreation, setManualCreation] = useState<{ open: boolean; profile: ProfileValues; result?: Contact }>({
    open: false,
    profile: defaultProfile,
  });

  const [status, setStatus] = useState<{ stepOne: StepStatuses; stepTwo: StepStatuses; stepThree: StepStatuses }>({
    stepOne: StepStatuses.ACTIVE,
    stepTwo: StepStatuses.INITIAL,
    stepThree: StepStatuses.INITIAL,
  });

  const { sessionCookie, liAtCookie, seatCookie } = useLinkedinCookies();

  const [searchURL, setSearchURL] = useState('');
  const [linkedinURLs, setLinkedinURLs] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [durationInfoDialog, setDurationInfoDialog] = useState<{ open: boolean; information: string; duration: string; warning: string }>({
    open: false,
    information: '',
    duration: '',
    warning: '',
  });

  const [selectedProjectWrapper, setSelectedProjectWrapper] = useState<OptionType | null>(null);
  const projectWrappers = useProjectWrappers('li_search');
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);

  const [maxContactsDialogOpen, setMaxContactsDialogOpen] = useState(false);
  const [cookieDialog, setCookieDialog] = useState<{
    open: boolean;
    liAtCookie: string;
    sessionCookie: string;
    seatCookie: string;
    error: CookieError | undefined;
  }>({
    open: false,
    liAtCookie: '',
    sessionCookie: '',
    seatCookie: '',
    error: undefined,
  });
  const [enterProjectNameDialog, setEnterProjectNameDialog] = useState<{ open: boolean; projectName: string; nameTakenError?: boolean }>({
    open: false,
    projectName: '',
  });
  const [enterPersonaNameDialog, setEnterPersonaNameDialog] = useState<{ open: boolean; personaName: string; nameTakenError?: boolean }>({
    open: false,
    personaName: '',
  });

  const [createGlobalProfileDialog, setCreateGlobalProfileDialog] = useState<{ open: boolean; profileName: string; nameTakenError?: boolean }>({
    open: false,
    profileName: '',
  });

  const [assessment, setAssessment] = useState<Assessment>();
  const [profile, setProfile] = useState<ProfileValues>();
  const [isSignificant, setIsSignificant] = useState<boolean>();
  const [significanceTestLoading, setSignificanceTestLoading] = useState(false);
  const [profileCreationLoading, setProfileCreationLoading] = useState(false);

  const [resultsPreview, setResultsPreview] = useState<{
    open: boolean;
    projectId?: string;
    filter: string[];
    sorting: { attribute: Attributes; direction: SortDirection } | undefined;
  }>({ open: false, filter: ['showAll'], sorting: undefined });

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const isLoading = searchLoading;

  useEffect(() => {
    setSelectedUrls(getProjectsUrls().strings);
  }, [urlFinderProjects]);

  useEffect(() => {
    if (classificationProjects.length > 0 || urlFinderProjects.length > 0) {
      setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.ACTIVE, stepThree: StepStatuses.INITIAL });
    }
  }, [classificationProjects, urlFinderProjects]);

  const resetPersonaGenerator = () => {
    setManualCreation({ open: false, profile: defaultProfile, result: undefined });
    setSelectedUrls([]);
    setLinkedinURLs([]);
    resetQuery();

    setAssessment(undefined);
    setProfile(undefined);
    setIsSignificant(undefined);

    setStatus({ stepOne: StepStatuses.ACTIVE, stepTwo: StepStatuses.INITIAL, stepThree: StepStatuses.INITIAL });
  };

  const backToStepOne = () => {
    setStatus({ stepOne: StepStatuses.ACTIVE, stepTwo: StepStatuses.INITIAL, stepThree: StepStatuses.INITIAL });
    setManualCreation({ open: false, profile: defaultProfile, result: undefined });
    setSelectedUrls([]);
    setLinkedinURLs([]);
    resetQuery();
  };

  const backToStepTwo = () => {
    setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.ACTIVE, stepThree: StepStatuses.INITIAL });
    setAssessment(undefined);
    setProfile(undefined);
    setIsSignificant(undefined);
  };

  const resetAll = () => {
    setStatus({ stepOne: StepStatuses.ACTIVE, stepTwo: StepStatuses.INITIAL, stepThree: StepStatuses.INITIAL });
    setManualCreation({ open: false, profile: defaultProfile, result: undefined });
    setSelectedUrls([]);
    setLinkedinURLs([]);
    resetQuery();
    setAssessment(undefined);
    setProfile(undefined);
    setIsSignificant(undefined);
  };

  /**
   * Search URL option
   */

  const handleSearchURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchURL(event.target.value);

    if (event.target.value === '') {
      setLinkedinURLs([]);
    }
  };

  const handleOpenDurationInfoDialog = (durationInfo: { information: string; duration: string; warning: string }) => {
    setDurationInfoDialog({ open: true, information: durationInfo.information, duration: durationInfo.duration, warning: durationInfo.warning });
  };

  const handleCloseDurationInfoDialog = () => {
    setDurationInfoDialog((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!durationInfoDialog.open) {
      setTimeout(() => {
        setDurationInfoDialog((prevState) => ({ ...prevState, information: '', duration: '', warning: '' }));
      }, 225);
    }
  }, [durationInfoDialog.open]);

  const findLinkedinUrlsWithSearchUrl = async () => {
    setSearchLoading(true);
    handleOpenDurationInfoDialog({
      information: t('linkedinProfileSearchDurationInfo'),
      duration: t('linkedinProfileSearchDurationTime'),
      warning: t('linkedinSearchDurationWarning'),
    });

    const res = await getLinkedinSearchResults(searchURL, liAtCookie, sessionCookie, seatCookie !== '' ? seatCookie : undefined);

    if (!res.error) {
      const urls = res.data;

      setLinkedinURLs(urls);
    } else {
      if (res.error.code === 401 && res.error.message === 'This account is not authorized to access the requested product.') {
        handleOpenLimitReachedDialog();
      } else if (res.error.code === 500) {
        handleOpenErrorDialog(res?.error, '/linkedinproject/getLinkedinSearchResults', 'post');
      } else {
        handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
      }

      setLinkedinURLs([]);
    }

    handleCloseDurationInfoDialog();
    setSearchLoading(false);
  };

  /**
   * Combine search projects option
   */

  const handleSelectedProjectWrapperChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => {
    setSelectedProjectWrapper(newValue);
  };

  const handleApplyProjectWrapperId = (wrapperId: string) => {
    return () => {
      setUrlFinderWrapperQuery(wrapperId);
      setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.ACTIVE, stepThree: StepStatuses.INITIAL });
    };
  };

  /**
   * Max contacts dialog handlers
   */

  const handleOpenMaxContactsDialog = () => {
    setMaxContactsDialogOpen(true);
  };

  const handleCloseMaxContactsDialog = () => {
    setMaxContactsDialogOpen(false);
  };

  /**
   * Cookie dialog handlers
   */

  const handleOpenCookieDialog = (msg: CookieErrors) => {
    return () => {
      setCookieDialog({ ...cookieDialog, open: true, error: { message: msg, cookie: liAtCookie } });
    };
  };

  const handleCloseCookieDialog = () => {
    setCookieDialog({ open: false, liAtCookie: '', sessionCookie: '', seatCookie: '', error: undefined });
  };

  /**
   * Enter project name dialog handlers
   */

  const handleOpenProjectNameDialog = () => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, open: true });
  };

  const handleCloseProjectNameDialog = () => {
    setEnterProjectNameDialog({ open: false, projectName: '', nameTakenError: undefined });
  };

  const handleProjectNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, projectName: event.target.value });
  };

  const handleAcceptProjectName = async () => {
    const tempUrls = !_.isEmpty(linkedinURLs) ? _.cloneDeep(linkedinURLs) : !_.isEmpty(selectedUrls) ? _.cloneDeep(selectedUrls) : [];
    const chunkArray = _.chunk(tempUrls, 150);

    const allRequests = chunkArray.map((urls, index) =>
      createLinkedinProject(
        chunkArray.length > 1 ? `${enterProjectNameDialog.projectName}#${index + 1}` : enterProjectNameDialog.projectName,
        urls
      )
    );

    const resProjects = await Promise.all(allRequests);

    if (!resProjects.some((request) => request.error)) {
      const projectIds = resProjects.map((request) => request.id);
      const resWrapper = await createProjectWrapper(enterProjectNameDialog.projectName, 'normal', projectIds);

      if (resWrapper && !resWrapper.error) {
        const newWrapper = resWrapper.projectWrapperId;
        setClassificationWrapperQuery(newWrapper);
        setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.ACTIVE, stepThree: StepStatuses.INITIAL });
        handleCloseProjectNameDialog();
        handleCloseMaxContactsDialog();
      } else {
        handleOpenErrorDialog(resWrapper?.error, '/projects/wrappers', 'post');
      }
    } else {
      if (
        resProjects.some(
          (request) =>
            request.error.code === 401 &&
            (request.error.message === 'This account is not authorized to access the requested product.' ||
              request.error.message === 'not Enough credits')
        )
      ) {
        handleOpenLimitReachedDialog();
      } else {
        resProjects.forEach((response) => {
          if (!_.isNil(response.error)) {
            handleOpenErrorDialog(response?.error, '/linkedinproject', 'post');
          }
        });
      }
    }
  };

  /**
   * Test significance
   */

  const significanceTested = () => {
    return !_.isNil(assessment) && !_.isNil(profile) && !_.isNil(isSignificant);
  };

  const handleTestSignificance = async () => {
    setSignificanceTestLoading(true);

    const selectedProjectIds = classificationProjects.filter((project) => project.analysisFinished).map((project) => project.id);
    const lang = i18n.languages[0];

    const res = await getGroupProfile(selectedProjectIds, lang);
    if (res && !res.error) {
      const assessment = res.assessment;
      const significance = res.significance;
      const profile = res.profile;

      setAssessment(assessment);
      setProfile(profile);
      setIsSignificant(significance.significant);

      setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.DONE, stepThree: StepStatuses.ACTIVE });
    } else {
      handleOpenErrorDialog(res?.error, '/linkedinproject/getGroupProfile', 'post');
    }
    setSignificanceTestLoading(false);
  };

  /**
   * Enter persona name dialog
   */

  const handleOpenPersonaNameDialog = () => {
    setEnterPersonaNameDialog({ ...enterPersonaNameDialog, open: true });
  };

  const handleClosePersonaNameDialog = () => {
    setEnterPersonaNameDialog({ open: false, personaName: '', nameTakenError: undefined });
  };

  const handlePersonaNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnterPersonaNameDialog({ ...enterPersonaNameDialog, personaName: event.target.value });
  };

  const handleSavePersona = async () => {
    if (!_.isNil(profile)) {
      setProfileCreationLoading(true);
      const res = await addProfile(enterPersonaNameDialog.personaName, profile);

      if (res && !res.error) {
        handleClosePersonaNameDialog();
        setProfileCreationLoading(false);
        handleOpenSuccessDialog();
      } else {
        setProfileCreationLoading(false);

        if (
          res.error.code === 401 &&
          (res.error.message === 'current license does not allow creating target groups' ||
            res.error.message === 'This account does not have enouth quota the requested product.')
        ) {
          handleOpenLimitReachedDialog();
        } else {
          handleOpenErrorDialog(res?.error, '/profiles', 'post');
        }
      }
    }
  };

  function getProjectsUrls() {
    const stringArray: string[] = [];
    const objectArray: { url: string; keyword: string }[] = [];
    const doublesArray: { url: string; keyword: string }[] = [];
    urlFinderProjects.forEach((project) =>
      project.searchKeywords.forEach((search) =>
        search.results.forEach((result, _index, results) => {
          if (!stringArray.includes(result)) {
            stringArray.push(result);
            objectArray.push({ url: result, keyword: search.keyword });
          }

          if (results.length > 1) {
            doublesArray.push({ url: result, keyword: search.keyword });
          }
        })
      )
    );

    return { strings: stringArray, objectsAll: objectArray, objectsDoubles: doublesArray };
  }

  const handleSelectionChange = (url: string) => {
    return () => {
      const updateSelectedUrls = [...selectedUrls];

      if (updateSelectedUrls.includes(url)) {
        _.pull(updateSelectedUrls, url);
      } else {
        updateSelectedUrls.push(url);
      }

      setSelectedUrls(updateSelectedUrls);
    };
  };

  /**
   * Manual profile creation
   */

  const handleManualCreationClick = () => {
    setManualCreation({ ...manualCreation, open: true });
  };

  const handleSliderChange = (value: number, variable: string) => {
    const updateManualCreation = { ...manualCreation };
    switch (variable) {
      case 'jointPosPower':
        updateManualCreation.profile[variable] = value;
        setManualCreation(updateManualCreation);
        break;
      case 'jointPosAchieve':
        updateManualCreation.profile[variable] = value;
        setManualCreation(updateManualCreation);
        break;
      case 'jointPosAffil':
        updateManualCreation.profile[variable] = value;
        setManualCreation(updateManualCreation);
        break;
      case 'authenticity':
        updateManualCreation.profile[variable] = value;
        setManualCreation(updateManualCreation);
        break;
      case 'regFocus':
        updateManualCreation.profile[variable] = value;
        setManualCreation(updateManualCreation);
        break;
    }
  };

  const handleSaveManualProfile = async () => {
    setProfileCreationLoading(true);
    const res = await addProfile(enterPersonaNameDialog.personaName, manualCreation.profile);

    if (res && !res.error) {
      const newProfile = res.id;
      const resContact = await getGeneralProfileResults(newProfile);

      if (resContact && !resContact.error) {
        setStatus({ stepOne: StepStatuses.DONE, stepTwo: StepStatuses.DONE, stepThree: StepStatuses.DONE });
        setManualCreation({ ...manualCreation, result: resContact.data });
        handleClosePersonaNameDialog();
        setProfileCreationLoading(false);
        handleOpenSuccessDialog();
      } else {
        setProfileCreationLoading(false);
        handleOpenErrorDialog(resContact?.error, '/profiles/base/:id', 'get');
      }
    } else {
      if (
        res.error.code === 401 &&
        (res.error.message === 'current license does not allow creating target groups' ||
          res.error.message === 'This account does not have enouth quota the requested product.')
      ) {
        handleOpenLimitReachedDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/profiles', 'post');
      }
      setProfileCreationLoading(false);
    }
  };

  /**
   * Create global profile dialog
   */

  const handleOpenCreateGlobalProfileDialog = () => {
    setCreateGlobalProfileDialog({ ...createGlobalProfileDialog, open: true });
  };

  const handleCloseCreateGlobalProfileDialog = () => {
    setCreateGlobalProfileDialog({ open: false, profileName: '', nameTakenError: undefined });
  };

  const handleGlobalProfileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateGlobalProfileDialog({ ...createGlobalProfileDialog, profileName: event.target.value });
  };

  const handleCreateNewGlobalProfile = async () => {
    setProfileCreationLoading(false);
    const selectedProjectIds = classificationProjects.filter((project) => project.analysisFinished).map((project) => project.id);

    const res = await createOccupation(createGlobalProfileDialog.profileName, selectedProjectIds);

    if (res && !res.error) {
      setProfileCreationLoading(false);
      handleCloseCreateGlobalProfileDialog();
      handleOpenSuccessDialog();
    } else {
      setProfileCreationLoading(false);
      handleOpenErrorDialog(res?.error, '/linkedin/lookups/occupations', 'post');
    }
  };

  /**
   * Linkedin project analysis handlers
   */

  const isAnalysisTodayPossible = (liAt: string | undefined, newAnalysesAmount: number) => {
    if (!_.isNil(liAt) && liAt !== '') {
      const storedAnalyses = localStorage.getItem('linkedin-analysis');
      const analysisArray: { startedAt: string; dailyLimit: number; liAt: string }[] = storedAnalyses ? JSON.parse(storedAnalyses) : [];
      const findAnalysis = analysisArray.find((analysis) => analysis.liAt === liAt);

      const storedLimit = findAnalysis?.dailyLimit;
      const storedTime = findAnalysis?.startedAt;

      if (!_.isNil(storedLimit) && !_.isNil(storedTime)) {
        const now = new Date();
        const storedTimeParsed: Date = parseISO(storedTime);
        const sameDay = isSameDay(now, storedTimeParsed);

        const restAvailable = storedLimit - newAnalysesAmount;

        if (!sameDay) {
          return true;
        } else if (sameDay && restAvailable >= 0) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    }
    return false;
  };

  const startAnalysis = (project: LinkedinProject) => {
    return async () => {
      if (isAnalysisTodayPossible(liAtCookie, project.linkedinContacts.length)) {
        if (liAtCookie !== '' && sessionCookie !== '') {
          handleOpenDurationInfoDialog({
            information: t('personaGeneratorDurationInfo'),
            duration: t('personaGeneratorDurationTime'),
            warning: t('personaGeneratorDurationWarning'),
          });

          const res = await analyzeLinkedinContacts(project.id, liAtCookie, sessionCookie, seatCookie);
          if (!res.error) {
            setTimeout(() => {
              fetchClassificationProjectsFromWrapper();
            }, 2000);

            const now = new Date();
            const storedAnalyses = localStorage.getItem('linkedin-analysis');
            const analysisArray: { startedAt: string; dailyLimit: number; liAt: string }[] = storedAnalyses ? JSON.parse(storedAnalyses) : [];
            const findAnalysis = analysisArray.find((analysis) => analysis.liAt === liAtCookie);
            const maxLimit = 150;
            if (!_.isNil(findAnalysis)) {
              const storedTimeParsed: Date = parseISO(findAnalysis.startedAt);
              const sameDay = isSameDay(now, storedTimeParsed);
              if (sameDay) {
                findAnalysis.dailyLimit -= project.linkedinContacts.length;
              } else {
                findAnalysis.startedAt = now.toISOString();
                findAnalysis.dailyLimit = maxLimit;
              }
            } else {
              analysisArray.push({
                startedAt: now.toISOString(),
                dailyLimit: maxLimit - project.linkedinContacts.length,
                liAt: liAtCookie || '',
              });
            }
            localStorage.setItem('linkedin-analysis', JSON.stringify(analysisArray));
          } else {
            if (res.error.code === 400 && res.error.message === 'Invalid Cookies provided') {
              handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
            } else {
              handleOpenErrorDialog(res?.error, '/linkedinproject/analyzeLinkedinContacts', 'post');
            }
          }
        } else {
          handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
        }
      } else {
        handleOpenCookieDialog(CookieErrors.HOUR_LIMIT)();
      }
    };
  };

  const handleRestartAnalysis = (project: LinkedinProject) => {
    return async () => {
      if (liAtCookie !== '' && sessionCookie !== '') {
        const res = await analyzeLinkedinContacts(project.id, liAtCookie, sessionCookie, seatCookie);
        if (!res.error) {
          setTimeout(() => {
            fetchClassificationProjectsFromWrapper();
          }, 1000);
        } else {
          if (res.error.code === 400 && res.error.message === 'Invalid Cookies provided') {
            handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
          } else {
            handleOpenErrorDialog(res?.error, '/linkedinproject/analyzeLinkedinContacts', 'post');
          }
        }
      } else {
        handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
      }
    };
  };

  const downloadOverallAnalysisResults = (id: string) => {
    return async () => {
      const res = await getAnalyzedLinkedinContacts(id);

      if (res && !res.error) {
        const newBlob = new Blob(['\ufeff', res], { type: 'text/csv' });
        saveAs(newBlob, `${t('linkedinContactAnalysis')}.csv`);
      } else {
        handleOpenErrorDialog(res?.error, '/linkedinproject/:linkedinProjectId/getAnalyzedContacts', 'get');
      }
    };
  };

  const downloadPersonaListAnalysisResults = (id: string) => {
    return async () => {
      const res = await getAnalyzedLinkedinContacts(id);

      const findProject = classificationProjects.find((project) => project.id === id);

      if (res && !res.error) {
        let textArr: string[] = res.split('\n');
        if (!textArr.length) {
          textArr = res.split('\n');
        }

        const columns = _.head(textArr)
          ?.split(';')
          .map((col) => col.replace(/['"]+/g, ''));

        if (!_.isNil(columns)) {
          const contactResults = _.tail(textArr);

          const mappedResults = contactResults.map((result) => {
            const tempObj: any = {};
            const splitResults = result.split(';').map((splitResult) => splitResult.replace(/['"]+/g, ''));

            columns.forEach((col, index) => {
              if (!_.isNil(splitResults[index]) && splitResults[index] !== '') {
                tempObj[col] = splitResults[index];
              }
            });

            return tempObj;
          });

          const tempObj: any = {};
          personas.forEach((persona) => {
            tempObj[persona] = mappedResults.filter((result) => result.type === persona);
          });

          const zip = new JSZip();

          _.forEach(tempObj, (value: any[], key: string) => {
            const content = value
              .map((item) => {
                let builtContent = '';
                columns.forEach((col) => {
                  if (!_.isNil(item[col])) {
                    builtContent += item[col] + ';';
                  } else {
                    builtContent += ';';
                  }
                });
                return builtContent;
              })
              .join('\n');

            const tempFile = columns.join(';') + '\n' + content;

            const newBlob = new Blob(['\ufeff', tempFile], { type: 'text/csv' });
            try {
              if (!_.isNil(findProject)) {
                zip.file(`${findProject.name}_${key}.csv`, newBlob);
              }
            } catch (e) {
              console.log(e);
            }
          });

          zip.generateAsync({ type: 'blob' }).then((content) => {
            saveAs(content, `${t('linkedinContactAnalysis')}.zip`);
          });
        }
      } else {
        handleOpenErrorDialog(res?.error, '/linkedinproject/:linkedinProjectId/getAnalyzedContacts', 'get');
      }
    };
  };

  /**
   * Results preview dialog handlers
   */

  const handleOpenResultsPreview = (projectId: string) => {
    return () => {
      setResultsPreview({ ...resultsPreview, open: true, projectId });
    };
  };

  const handleCloseResultsPreview = () => {
    setResultsPreview({ open: false, projectId: undefined, filter: ['showAll'], sorting: undefined });
  };

  const handleFilterChange = (currFilter: string) => {
    return () => {
      const updateResultsPreview = { ...resultsPreview };
      let updateFilter = _.cloneDeep(resultsPreview.filter);
      if (updateFilter.includes(currFilter) && currFilter !== 'showAll') {
        _.pull(updateFilter, currFilter);
      } else {
        updateFilter.push(currFilter);
      }

      if (updateFilter.includes('showAll') && currFilter !== 'showAll') {
        _.pull(updateFilter, 'showAll');
      }

      if (!updateFilter.includes('showAll') && currFilter !== 'showAll' && updateFilter.length === 0) {
        updateFilter.push('showAll');
      }

      if (currFilter === 'showAll') {
        updateFilter = ['showAll'];
      }

      updateResultsPreview.filter = updateFilter;

      setResultsPreview(updateResultsPreview);
    };
  };

  const handleSortChange = (attribute: Attributes) => {
    return () => {
      const updateResultsPreview = { ...resultsPreview };
      let updateSorting = _.cloneDeep(resultsPreview.sorting);
      if (_.isNil(updateSorting)) {
        updateSorting = { attribute, direction: SortDirection.ASC };
      } else {
        if (updateSorting?.attribute === attribute && updateSorting.direction === SortDirection.ASC) {
          updateSorting.direction = SortDirection.DESC;
        } else if (updateSorting?.attribute !== attribute) {
          updateSorting = { attribute, direction: SortDirection.ASC };
        } else {
          updateSorting = undefined;
        }
      }

      updateResultsPreview.sorting = updateSorting;

      setResultsPreview(updateResultsPreview);
    };
  };

  /**
   * Success dialog handlers
   */

  const handleOpenSuccessDialog = () => {
    setSuccessDialogOpen(true);
  };

  const handleConfirmSuccess = () => {
    resetPersonaGenerator();
    setSuccessDialogOpen(false);
  };

  return (
    <>
      {appBarNavigation === NavigationStates.TOOL ? (
        <div className={classes.container}>
          <PaiActionText text={t('back')} marginBottom={'8px'} backArrow={true} action={() => navigate(-1)} />
          <Stack className={classes.steps} direction={{ xs: 'column', lg: 'row' }} gap={1}>
            <div className={cx(classes.step, 'small')}>
              <div className={classes.titleContainer}>
                <Typography className={classes.stepTitle}>{t('firstStep')}</Typography>
                <Typography className={classes.stepTitle}>{t('addProfilesToPersona')}</Typography>
                {status.stepOne === StepStatuses.DONE ? <CheckCircleIcon className={classes.icon} /> : null}
              </div>
              <DefineLinkedinProfiles
                searchURL={searchURL}
                linkedinURLs={linkedinURLs}
                loading={isLoading}
                disabled={status.stepOne === StepStatuses.DONE}
                projectWrappers={projectWrappers}
                selectedProjectWrapper={selectedProjectWrapper}
                handleSearchURLChange={handleSearchURLChange}
                findLinkedinUrlsWithSearchUrl={findLinkedinUrlsWithSearchUrl}
                handleOpenProjectNameDialog={handleOpenProjectNameDialog}
                handleSelectedProjectWrapperChange={handleSelectedProjectWrapperChange}
                handleApplyProjectWrapperId={handleApplyProjectWrapperId}
                handleManualCreationClick={handleManualCreationClick}
              />
            </div>
            <div className={cx(classes.step, urlFinderProjects.length > 0 ? 'very-large' : 'large')}>
              {!manualCreation.open ? (
                <div className={classes.titleContainer}>
                  <Typography className={classes.stepTitle}>{t('secondStep')}</Typography>
                  <Typography className={classes.stepTitle}>{t('classifyPersonalityTypes')}</Typography>
                  {status.stepTwo === StepStatuses.DONE ? <CheckCircleIcon className={classes.icon} /> : null}
                  {status.stepOne === StepStatuses.DONE && status.stepTwo !== StepStatuses.DONE ? (
                    <div className={classes.backButton}>
                      <PaiLink text={t('back')} size={'xs'} underline={'always'} disabled={isLoading} action={backToStepOne} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className={classes.titleContainer}>
                  <Typography className={classes.stepTitle}>{t('secondStep')}</Typography>
                  <Typography className={classes.stepTitle}>{t('definePersonalityTypeManually')}</Typography>
                  {!_.isNil(manualCreation.result) ? <CheckCircleIcon className={classes.icon} /> : null}
                  <div className={classes.backButton}>
                    <PaiLink text={t('back')} size={'xs'} underline={'always'} disabled={isLoading} action={backToStepOne} />
                  </div>
                </div>
              )}
              {manualCreation.open ? (
                <CreateManualProfile
                  profile={manualCreation.profile}
                  handleSliderChange={handleSliderChange}
                  handleOpenPersonaNameDialog={handleOpenPersonaNameDialog}
                />
              ) : status.stepTwo === StepStatuses.INITIAL ? (
                <PlaceholderElement text={t('personaGeneratorStepTwoPlaceholder')} />
              ) : status.stepTwo === StepStatuses.ACTIVE || status.stepTwo === StepStatuses.DONE ? (
                urlFinderProjects.length > 0 ? (
                  <StepTwoUrlFinderProjects
                    selectedUrls={selectedUrls}
                    getProjectsUrls={getProjectsUrls}
                    handleOpenProjectNameDialog={handleOpenProjectNameDialog}
                    handleSelectionChange={handleSelectionChange}
                  />
                ) : (
                  <EvaluateClassificationProjects
                    classificationProjectName={classificationProjectName}
                    classificationProjects={classificationProjects}
                    isSignificant={isSignificant}
                    significanceTestLoading={significanceTestLoading}
                    handleTestSignificance={handleTestSignificance}
                    significanceTested={significanceTested}
                    startAnalysis={startAnalysis}
                    handleRestartAnalysis={handleRestartAnalysis}
                    downloadOverallAnalysisResults={downloadOverallAnalysisResults}
                    downloadPersonaListAnalysisResults={downloadPersonaListAnalysisResults}
                    handleOpenResultsPreview={handleOpenResultsPreview}
                  />
                )
              ) : null}
            </div>
            {urlFinderProjects.length === 0 ? (
              <div className={cx(classes.step, 'large')}>
                <div className={classes.titleContainer}>
                  <Typography className={classes.stepTitle}>{t('thirdStep')}</Typography>
                  <Typography className={classes.stepTitle}>{!manualCreation.open ? t('evaluationResult') : t('result')}</Typography>
                  {status.stepThree === StepStatuses.DONE ? <CheckCircleIcon className={classes.icon} /> : null}
                  {status.stepTwo === StepStatuses.DONE && status.stepThree !== StepStatuses.DONE ? (
                    <div className={classes.backButton}>
                      <PaiLink text={t('back')} size={'xs'} underline={'always'} disabled={isLoading} action={backToStepTwo} />
                    </div>
                  ) : null}
                </div>
                {!_.isNil(manualCreation.result) ? (
                  <CreateManualProfileSuccess profileName={manualCreation.result.name} assessment={manualCreation.result.assessment} />
                ) : status.stepThree === StepStatuses.INITIAL ? (
                  <PlaceholderElement text={t('personaGeneratorStepThreePlaceholder')} />
                ) : status.stepThree === StepStatuses.ACTIVE ? (
                  <CreateProfile
                    assessment={assessment}
                    isSignificant={isSignificant}
                    handleOpenPersonaNameDialog={handleOpenPersonaNameDialog}
                    handleOpenCreateGlobalProfileDialog={handleOpenCreateGlobalProfileDialog}
                  />
                ) : null}
              </div>
            ) : null}
          </Stack>
        </div>
      ) : appBarNavigation === NavigationStates.PROJECTS ? (
        <LinkedinCrawlerOverview title={t('personaGenerator')} type={'normal'} resetAll={resetAll} />
      ) : null}

      <MaxContactsDialog open={maxContactsDialogOpen} handleClose={handleCloseMaxContactsDialog} handleAcceptProjectName={handleAcceptProjectName} />
      <CookieDialog open={cookieDialog.open} error={cookieDialog.error} handleClose={handleCloseCookieDialog} />
      <EnterProjectNameDialog
        open={enterProjectNameDialog.open}
        projectName={enterProjectNameDialog.projectName}
        nameTakenError={enterProjectNameDialog.nameTakenError}
        handleProjectNameChange={handleProjectNameChange}
        handleClose={handleCloseProjectNameDialog}
        acceptProjectName={linkedinURLs.length > 150 ? handleOpenMaxContactsDialog : handleAcceptProjectName}
      />
      <EnterProfileNameDialog
        open={enterPersonaNameDialog.open}
        personaName={enterPersonaNameDialog.personaName}
        nameTakenError={enterPersonaNameDialog.nameTakenError}
        loading={profileCreationLoading}
        handlePersonaNameChange={handlePersonaNameChange}
        handleClose={handleClosePersonaNameDialog}
        savePersona={manualCreation.open ? handleSaveManualProfile : handleSavePersona}
      />
      <CreateGlobalProfileDialog
        open={createGlobalProfileDialog.open}
        profileName={createGlobalProfileDialog.profileName}
        nameTakenError={createGlobalProfileDialog.nameTakenError}
        loading={profileCreationLoading}
        handleGlobalProfileNameChange={handleGlobalProfileNameChange}
        handleClose={handleCloseCreateGlobalProfileDialog}
        handleCreateNewGlobalProfile={handleCreateNewGlobalProfile}
      />
      <ProjectResultPreview
        open={resultsPreview.open}
        projectId={resultsPreview.projectId}
        filter={resultsPreview.filter}
        sorting={resultsPreview.sorting}
        handleFilterChange={handleFilterChange}
        handleSortChange={handleSortChange}
        handleCloseDialog={handleCloseResultsPreview}
      />
      <PaiSuccessDialog
        open={successDialogOpen}
        title={t('personaSuccessfullyCreated')}
        content={t('personaSuccessfullyCreatedText')}
        handleConfirm={handleConfirmSuccess}
      />
      <SearchDurationInfoDialog
        open={durationInfoDialog.open}
        information={durationInfoDialog.information}
        duration={durationInfoDialog.duration}
        warning={durationInfoDialog.warning}
        handleClose={handleCloseDurationInfoDialog}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'inline-block',
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    width: '100%',
    maxHeight: 'calc(960px - 60px - 32px)',
    [theme.breakpoints.down('lg')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  steps: {
    display: 'flex',
    height: 'calc(100% - 28px)',
  },
  step: {
    flexBasis: '100%',
    '&.small': {
      flexBasis: '25%',
      [theme.breakpoints.down('xl')]: {
        flexBasis: '33%',
      },
      [theme.breakpoints.down('lg')]: {
        flexBasis: '100%',
      },
    },
    '&.large': {
      flexBasis: '37.5%',
      [theme.breakpoints.down('xl')]: {
        flexBasis: '33%',
      },
      [theme.breakpoints.down('lg')]: {
        flexBasis: '100%',
      },
    },
    '&.very-large': {
      flexBasis: '75%',
      [theme.breakpoints.down('xl')]: {
        flexBasis: '66%',
      },
      [theme.breakpoints.down('lg')]: {
        flexBasis: '100%',
      },
    },
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px',
    height: '100%',
  },
  titleContainer: {
    marginBottom: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  stepTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  icon: {
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: theme.typography.pxToRem(40),
    color: theme.palette.success.main,
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
}));

export default ProfileGenerator;
